import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { forwardRef, memo, type ReactNode, useCallback, useEffect, useState } from 'react';

import { usePortalStore } from '@air/primitive-portal';
import { TooltipContent, TooltipRoot, TooltipTrigger } from '@air/primitive-tooltip';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const dropdownMenu = tailwindVariants({
  base: 'z-portal flex max-h-[var(--radix-dropdown-menu-content-available-height)] w-[216px] min-w-[var(--radix-popper-anchor-width)] radix-side-bottom:animate-slide-up radix-side-top:animate-slide-down flex-col gap-2 overflow-y-auto rounded-8 ads:bg-raised bg-surface-1 p-2 ads:text-primary text-gray-12 shadow-[0_0_0_1px_rgba(0,0,0,0.05),0_3px_6px_0_rgba(0,0,0,0.10),0_9px_24px_0_rgba(0,0,0,0.20)] outline-none dark:border dark:border-gray-5',
  defaultVariants: {
    size: 'small',
  },
  variants: {
    size: {
      large: 'w-[240px]',
      small: 'w-[216px]',
    },
  },
});

export type DropdownMenuVariant = VariantProps<typeof dropdownMenu>;

export type DropdownMenuProps = Pick<RadixDropdownMenu.DropdownMenuProps, 'onOpenChange' | 'open'> &
  Pick<RadixDropdownMenu.DropdownMenuTriggerProps, 'disabled'> &
  RadixDropdownMenu.DropdownMenuContentProps &
  DropdownMenuVariant & {
    trigger?: RadixDropdownMenu.DropdownMenuTriggerProps['children'];
    container?: RadixDropdownMenu.DropdownMenuPortalProps['container'];
    /**
     * When set, the trigger also shows a tooltip with this content. Composed so
     * the same element is both the tooltip and dropdown trigger.
     */
    triggerTooltip?: ReactNode;
  };

export const DropdownMenu = memo(
  forwardRef<HTMLDivElement, DropdownMenuProps>(
    (
      {
        align = 'start',
        alignOffset,
        children,
        className,
        collisionPadding = 8,
        container,
        disabled,
        loop,
        onOpenChange,
        open = false,
        side = 'bottom',
        sideOffset = 8,
        size = 'small',
        trigger,
        triggerTooltip,
        onEscapeKeyDown,
        ...restOfProps
      },
      ref,
    ) => {
      const portal = usePortalStore((state) => state.portal);
      const [isMenuOpened, setIsMenuOpened] = useState(open);

      useEffect(() => {
        setIsMenuOpened(open);
      }, [open]);

      const handleMenuOpenChange = useCallback(
        (nextOpen: boolean) => {
          setIsMenuOpened(nextOpen);
          onOpenChange?.(nextOpen);
        },
        [onOpenChange],
      );

      const root = (
        <RadixDropdownMenu.Root
          data-theme="dark"
          dir="ltr"
          modal={false}
          onOpenChange={handleMenuOpenChange}
          open={isMenuOpened}
        >
          {/* Nest the tooltip trigger under the dropdown trigger so both attach to the same element. */}
          <RadixDropdownMenu.Trigger asChild disabled={disabled}>
            {triggerTooltip ? <TooltipTrigger asChild>{trigger}</TooltipTrigger> : trigger}
          </RadixDropdownMenu.Trigger>

          <RadixDropdownMenu.Portal container={container || portal}>
            <RadixDropdownMenu.Content
              align={align}
              alignOffset={alignOffset}
              className={dropdownMenu({ className, size })}
              collisionPadding={collisionPadding}
              data-testid="DROPDOWN_MENU"
              loop={loop}
              onEscapeKeyDown={onEscapeKeyDown}
              ref={ref}
              side={side}
              sideOffset={sideOffset}
              {...restOfProps}
            >
              {children}
            </RadixDropdownMenu.Content>
          </RadixDropdownMenu.Portal>
        </RadixDropdownMenu.Root>
      );

      if (!triggerTooltip) {
        return root;
      }

      return (
        <TooltipRoot>
          {root}
          <TooltipContent>{triggerTooltip}</TooltipContent>
        </TooltipRoot>
      );
    },
  ),
);

DropdownMenu.displayName = 'DropdownMenu';
